/**
 * EntityCard - Card for displaying entity class with instances and AI suggestions button
 * Domain-colored with context-aware AI suggestions (reuses entity suggestions service)
 */

import { Plus, Check, Sparkles, Users, Target, Lightbulb, TrendingUp, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EntityDomain } from '@ispot/shared'
import type { EntityTreeInstance } from '@/lib/api/client'

export interface AISuggestion {
  id: string
  label: string
  reason?: string
  confidence?: number
}

interface EntityCardProps {
  classUri: string
  className: string
  domain: EntityDomain
  description?: string
  isRequired: boolean
  minCount: number
  currentCount: number
  instances: EntityTreeInstance[]
  onAddEntity?: (classUri: string) => void
  /** Callback when AI suggestions button is clicked */
  onAISuggestionsClick?: (classUri: string, className: string, domain: EntityDomain) => void
  /** Currently loaded suggestions for this entity type */
  suggestions?: AISuggestion[]
  /** Loading state for suggestions */
  isLoadingSuggestions?: boolean
  /** Callback when a suggestion is accepted */
  onAcceptSuggestion?: (suggestion: AISuggestion, classUri: string) => void
}

// Domain colors
const DOMAIN_STYLES: Record<EntityDomain, { border: string; bg: string; text: string }> = {
  market: {
    border: 'border-l-market',
    bg: 'bg-market/10',
    text: 'text-market',
  },
  business: {
    border: 'border-l-business',
    bg: 'bg-business/10',
    text: 'text-business',
  },
  solution: {
    border: 'border-l-solution',
    bg: 'bg-solution/10',
    text: 'text-solution',
  },
}

// Icons by entity class name
const CLASS_ICONS: Record<string, React.ReactNode> = {
  Industry: <TrendingUp className="w-4 h-4" />,
  CustomerSegment: <Users className="w-4 h-4" />,
  Persona: <Users className="w-4 h-4" />,
  CustomerPain: <Target className="w-4 h-4" />,
  CustomerJob: <Target className="w-4 h-4" />,
  Competitor: <Target className="w-4 h-4" />,
  Trend: <TrendingUp className="w-4 h-4" />,
  ValueProposition: <Lightbulb className="w-4 h-4" />,
  Feature: <Lightbulb className="w-4 h-4" />,
}

export function EntityCard({
  classUri,
  className,
  domain,
  description,
  isRequired,
  minCount,
  currentCount,
  instances,
  onAddEntity,
  onAISuggestionsClick,
  suggestions,
  isLoadingSuggestions,
  onAcceptSuggestion,
}: EntityCardProps) {
  // Defensive: default to 'market' if domain is invalid
  const domainStyle = DOMAIN_STYLES[domain] || DOMAIN_STYLES.market
  // Show all non-rejected instances (pending + confirmed) to match the count display
  const activeInstances = instances.filter(i => i.status !== 'rejected')
  const isComplete = currentCount >= minCount && minCount > 0
  const hasSuggestions = suggestions && suggestions.length > 0

  return (
    <div
      className={cn(
        'bg-white border border-border-subtle rounded-lg overflow-hidden transition-all hover:shadow-md',
        'border-l-4',
        domainStyle.border
      )}
    >
      {/* Header - compact */}
      <div className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <div className={cn('w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0', domainStyle.bg, domainStyle.text)}>
            {CLASS_ICONS[className] || <Target className="w-4 h-4" />}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-text truncate">{formatClassName(className)}</span>
              {isRequired && (
                <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-market/15 text-market flex-shrink-0">
                  Required
                </span>
              )}
            </div>
            {description && (
              <p className="text-[11px] text-text-muted line-clamp-1 mt-0.5">{description}</p>
            )}
          </div>

          {/* Count Badge */}
          <div
            className={cn(
              'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0',
              isComplete ? 'bg-market/10 text-market' : 'bg-warm-100 text-warm-600'
            )}
          >
            {isComplete && <Check className="w-3 h-3" />}
            {minCount > 0 ? `${currentCount}/${minCount}` : currentCount}
          </div>
        </div>
      </div>

      {/* Instances + Add button - inline */}
      <div className="px-3 pb-2">
        <div className="flex flex-wrap gap-1.5">
          {activeInstances.slice(0, 3).map((instance) => (
            <span
              key={instance.uri}
              className={cn(
                'px-2 py-1 rounded text-[11px] font-medium truncate max-w-[120px]',
                instance.status === 'confirmed'
                  ? 'bg-market/10 text-market'
                  : 'bg-warm-100 text-warm-700'
              )}
              title={instance.status === 'pending' ? 'Pending validation' : 'Confirmed'}
            >
              {instance.label}
            </span>
          ))}
          {activeInstances.length > 3 && (
            <span className="px-2 py-1 bg-warm-100 rounded text-[11px] font-medium text-warm-500">
              +{activeInstances.length - 3}
            </span>
          )}
          <button
            onClick={() => onAddEntity?.(classUri)}
            className="flex items-center gap-1 px-2 py-1 border border-dashed border-border rounded text-[11px] font-medium text-market hover:bg-market/5 hover:border-market transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add
          </button>
        </div>
      </div>

      {/* AI Suggestions - compact inline */}
      <div className="px-3 py-2 border-t border-border-subtle bg-warm-50/50">
        {isLoadingSuggestions ? (
          <div className="flex items-center gap-1.5 text-[11px] text-text-muted">
            <Loader2 className="w-3 h-3 animate-spin" />
            Getting suggestions...
          </div>
        ) : hasSuggestions ? (
          <div>
            <div className="flex items-center gap-1 text-[10px] font-semibold text-market mb-1.5">
              <Sparkles className="w-3 h-3" />
              AI Suggests
            </div>
            <div className="flex flex-wrap gap-1">
              {suggestions!.slice(0, 4).map((suggestion) => (
                <button
                  key={suggestion.id}
                  onClick={() => onAcceptSuggestion?.(suggestion, classUri)}
                  className="group flex items-center gap-1 px-2 py-1 bg-white border border-border-subtle rounded text-[11px] text-warm-700 hover:border-market hover:bg-market/5 transition-all"
                  title={suggestion.reason || 'Click to add'}
                >
                  <span className="truncate max-w-[100px]">{suggestion.label}</span>
                  <Plus className="w-3 h-3 text-market opacity-50 group-hover:opacity-100" />
                </button>
              ))}
              {suggestions!.length > 4 && (
                <span className="px-2 py-1 text-[11px] text-text-muted">
                  +{suggestions!.length - 4} more
                </span>
              )}
            </div>
          </div>
        ) : onAISuggestionsClick ? (
          <button
            onClick={() => onAISuggestionsClick(classUri, className, domain)}
            className="flex items-center gap-1 text-[11px] font-medium text-market hover:underline"
          >
            <Sparkles className="w-3 h-3" />
            Get AI Suggestions
          </button>
        ) : null}
      </div>
    </div>
  )
}

// Format class name for display
function formatClassName(name: string): string {
  // Split camelCase/PascalCase
  return name.replace(/([a-z])([A-Z])/g, '$1 $2')
}
